/**
 * EnhancedLetterPreview - Professional Step 5 Letter Preview Component
 * 
 * P0 Features:
 * - Bureau tabs WITHOUT colored dots
 * - Real item counts from backend
 * - Action buttons: Back to Revise, Print, Download PDF (NO Copy)
 * - Preview/PDF/Print margin parity (0.5in top/bottom, 0.6in left/right)
 * 
 * P1 Features:
 * - Optional typed digital signature
 * 
 * IMPORTANT: UI-only enhancements. Does NOT modify letter content.
 * All helper cards are preview-only and do NOT appear in PDF.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  injectPrintablePreviewCss,
  resolvePrintablePreviewHtml,
} from '../../utils/generationResultUtils';
import {
  FileText,
  Download,
  ChevronLeft,
  Printer,
  CheckCircle,
  Shield,
  Loader2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  User,
  MapPin,
  Calendar,
  Info,
  ChevronDown,
  ChevronUp,
  FileCheck,
  Lock,
  BadgeCheck,
  Layers,
  PenTool,
  Check,
  Building2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

// ============================================
// HELPER: Calculate actual item count from letter
// IMPORTANT: Do NOT use fallback = 1
// ============================================
const getActualItemCount = (letter) => {
  // DEBUG: Log item count sources
  console.log(`[ITEM_COUNT_DEBUG] Letter ${letter?.bureau}:`, {
    item_count: letter?.item_count,
    items_count: letter?.items_count,
    disputed_items_length: letter?.disputed_items?.length,
    item_breakdown: letter?.item_breakdown,
    first_3_items: letter?.disputed_items?.slice(0, 3).map(i => i?.creditor || i?.field_label || i?.type || 'unknown')
  });
  
  // Priority 1: Use backend-provided item_count (most reliable)
  if (typeof letter?.item_count === 'number' && letter.item_count > 0) {
    console.log(`[ITEM_COUNT_DEBUG] Using backend item_count: ${letter.item_count}`);
    return letter.item_count;
  }
  
  // Priority 2: Use item_breakdown.total
  if (letter?.item_breakdown?.total && letter.item_breakdown.total > 0) {
    console.log(`[ITEM_COUNT_DEBUG] Using item_breakdown.total: ${letter.item_breakdown.total}`);
    return letter.item_breakdown.total;
  }
  
  // Priority 3: Use items_proceeding_count from package
  if (letter?.items_proceeding_count && letter.items_proceeding_count > 0) {
    console.log(`[ITEM_COUNT_DEBUG] Using items_proceeding_count: ${letter.items_proceeding_count}`);
    return letter.items_proceeding_count;
  }
  
  // Priority 4: Count disputed_items array
  if (letter?.disputed_items?.length > 0) {
    console.log(`[ITEM_COUNT_DEBUG] Using disputed_items.length: ${letter.disputed_items.length}`);
    return letter.disputed_items.length;
  }
  
  // Priority 5: Count from various item arrays
  let count = 0;
  count += letter?.account_items?.length || 0;
  count += letter?.inquiry_items?.length || 0;
  count += letter?.personal_info_items?.length || 0;
  count += letter?.pi_items?.length || 0;
  
  if (count > 0) {
    console.log(`[ITEM_COUNT_DEBUG] Using aggregated count: ${count}`);
    return count;
  }
  
  // NO FALLBACK TO 1 - If we can't determine count, return 0
  console.warn(`[ITEM_COUNT_DEBUG] WARNING: Could not determine item count for ${letter?.bureau}, returning 0`);
  return 0;
};

// ============================================
// BUREAU TAB SELECTOR - NO COLORED DOTS
// ============================================
const BureauTabSelector = ({ letters, currentIndex, onSelectLetter }) => {
  if (!letters || letters.length <= 1) return null;
  
  const getMethodLabel = (letter) => {
    const method = letter.method_used || letter.package_method || 'factual';
    const methodLabels = {
      'data_breach': 'Data Breach',
      'data_breach_method': 'Data Breach',
      'personal_info_only': 'PI Accuracy',
      'personal_info_accuracy': 'PI Accuracy',
      'isolated': 'Isolated',
      'isolated_method': 'Isolated',
      'structural': 'Structural',
      'recovery': 'Recovery',
      'factual_inaccuracy': 'Factual',
    };
    return methodLabels[method] || method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  return (
    <div className="-mx-1 px-1 sm:mx-0 sm:px-0 overflow-x-auto pb-1 mb-4 sm:overflow-visible sm:pb-0" data-testid="bureau-tab-selector">
      <div className="flex gap-2 min-w-max sm:min-w-0 sm:flex-wrap sm:mb-2">
      {letters.map((letter, index) => {
        const isActive = index === currentIndex;
        const bureau = letter.bureau || 'Unknown';
        const method = getMethodLabel(letter);
        const itemCount = getActualItemCount(letter);
        
        return (
          <button
            key={`${bureau}-${index}`}
            onClick={() => onSelectLetter(index)}
            className={`
              relative flex items-center gap-2 sm:gap-3 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border-2 transition-all shrink-0 min-w-[148px] max-w-[220px] sm:min-w-0 sm:max-w-none
              ${isActive 
                ? 'border-emerald-500 dark:border-emerald-400 bg-emerald-50 dark:bg-[rgba(16,185,129,0.15)] shadow-md' 
                : 'border-stone-200 dark:border-[rgba(142,218,182,0.20)] bg-white dark:bg-[rgba(10,38,28,0.50)] hover:border-stone-300 dark:hover:border-[rgba(142,218,182,0.35)] hover:shadow-sm'
              }
            `}
            data-testid={`bureau-tab-${bureau.toLowerCase()}`}
          >
            {/* Bureau name and method - NO colored dot */}
            <div className="text-left min-w-0 flex-1">
              <div className="font-semibold text-sm sm:text-base text-stone-900 dark:text-[#EAF7F0] truncate">{bureau}</div>
              <div className="text-xs text-stone-500 dark:text-[#93AE9F] truncate">{method}</div>
            </div>
            
            {/* Item count badge - show actual count */}
            <Badge variant="outline" className="text-xs shrink-0 dark:bg-[rgba(10,38,28,0.50)] dark:text-[#C7DDD0] dark:border-[rgba(142,218,182,0.20)]">
              {itemCount} item{itemCount !== 1 ? 's' : ''}
            </Badge>
            
            {/* Ready indicator */}
            {(letter.preview_html || letter.html) && (
              <CheckCircle size={16} className="text-emerald-500 dark:text-emerald-400 shrink-0" />
            )}
            
            {/* Active indicator bar */}
            {isActive && (
              <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-8 h-1 bg-emerald-500 dark:bg-emerald-400 rounded-full" />
            )}
          </button>
        );
      })}
      </div>
    </div>
  );
};

// ============================================
// PREVIEW HEADER - NO COPY BUTTON
// ============================================
const PreviewHeader = ({ 
  letter, 
  totalLetters, 
  currentIndex,
  onDownload, 
  onPrint, 
  onBack,
  downloading,
  backButtonLabel = "Back to Revise"
}) => {
  const bureau = letter?.bureau || 'Unknown';
  const method = letter?.method_used || letter?.package_method || 'factual';
  const itemCount = getActualItemCount(letter);
  
  const methodLabels = {
    'data_breach': 'Data Breach',
    'data_breach_method': 'Data Breach',
    'personal_info_only': 'PI Accuracy',
    'personal_info_accuracy': 'PI Accuracy',
    'isolated': 'Isolated Strategy',
    'isolated_method': 'Isolated Strategy',
    'structural': 'Structural',
    'recovery': 'Recovery',
    'factual_inaccuracy': 'Factual',
  };
  
  const displayMethod = methodLabels[method] || method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  
  return (
    <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)] rounded-xl p-3 sm:p-5 mb-4 shadow-sm min-w-0" data-testid="preview-header">
      <div className="flex flex-col gap-4 min-w-0">
        {/* Letter Info */}
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-bold text-stone-900 dark:text-[#EAF7F0] flex flex-wrap items-center gap-2">
            <FileText size={22} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
            Letter Preview
            {totalLetters > 1 && (
              <Badge variant="outline" className="text-xs dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]">
                {currentIndex + 1} of {totalLetters}
              </Badge>
            )}
          </h2>
          
          <div className="mt-2 grid grid-cols-1 gap-2 min-[400px]:grid-cols-3 sm:flex sm:flex-wrap sm:items-center sm:gap-x-6 sm:gap-y-2 text-sm">
            <div className="min-w-0">
              <span className="text-slate-600 dark:text-[#C7DDD0]">Bureau:</span>
              <span className="ml-2 font-medium text-slate-900 dark:text-[#F7C948] break-words">{bureau}</span>
            </div>
            <div className="min-w-0">
              <span className="text-slate-600 dark:text-[#C7DDD0]">Method:</span>
              <span className="ml-2 font-medium text-slate-900 dark:text-[#F7C948] break-words">{displayMethod}</span>
            </div>
            <div>
              <span className="text-slate-600 dark:text-[#C7DDD0]">Items:</span>
              <span className="ml-2 font-medium text-slate-900 dark:text-[#F7C948]">{itemCount}</span>
            </div>
          </div>
        </div>
        
        {/* Action Buttons - NO COPY BUTTON */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 w-full">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="w-full sm:w-auto text-stone-600 dark:text-[#C7DDD0] dark:border-[rgba(142,218,182,0.20)]"
            data-testid="back-to-revise-btn"
          >
            <ChevronLeft size={16} className="mr-1" />
            {backButtonLabel}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={onPrint}
            className="w-full sm:w-auto text-stone-600 dark:text-[#C7DDD0] dark:border-[rgba(142,218,182,0.20)]"
            data-testid="print-letter-btn"
          >
            <Printer size={16} className="mr-1" />
            Print
          </Button>
          
          <Button
            onClick={onDownload}
            disabled={downloading}
            className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="download-pdf-btn"
          >
            {downloading ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <Download size={16} className="mr-2" />
            )}
            Download PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// LETTER SUMMARY CARD - ACCURATE COUNTS
// ============================================
const LetterSummaryCard = ({ letter, protectedAnchors }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  const bureau = letter?.bureau || 'Unknown';
  const method = letter?.method_used || letter?.package_method || 'factual';
  const totalItemCount = getActualItemCount(letter);
  
  // Use backend-provided item_breakdown first, then fallback to computing from disputed_items
  const backendBreakdown = letter?.item_breakdown;
  
  // Extract item counts by type from disputed_items if backend breakdown not available
  const disputedItems = letter?.disputed_items || [];
  
  // Helper function to count items by type locally
  const countItemsByType = (filterFn) => {
    return disputedItems.filter(filterFn).length;
  };
  
  // Use backend breakdown if available, otherwise compute locally
  const accountCount = backendBreakdown?.accounts !== undefined 
    ? backendBreakdown.accounts 
    : countItemsByType(i => i.type === 'account' || i.item_type === 'account');
  
  const inquiryCount = backendBreakdown?.inquiries !== undefined 
    ? backendBreakdown.inquiries 
    : countItemsByType(i => i.type === 'inquiry' || i.item_type === 'inquiry');
  
  const addressCount = backendBreakdown?.addresses !== undefined 
    ? backendBreakdown.addresses 
    : countItemsByType(i => 
        i.field_type === 'address' || i.item_type === 'address' || 
        i.field_label?.toLowerCase().includes('address')
      );
  
  const nameCount = backendBreakdown?.names_aliases !== undefined 
    ? backendBreakdown.names_aliases 
    : countItemsByType(i => 
        i.field_type === 'name' || i.item_type === 'name' ||
        i.field_label?.toLowerCase().includes('name') || i.field_label?.toLowerCase().includes('alias')
      );
  
  const dobCount = backendBreakdown?.dob !== undefined 
    ? backendBreakdown.dob 
    : countItemsByType(i => 
        i.field_type === 'dob' || i.item_type === 'dob' ||
        i.field_label?.toLowerCase().includes('birth') || i.field_label?.toLowerCase().includes('dob')
      );
  
  const employerCount = backendBreakdown?.employers !== undefined 
    ? backendBreakdown.employers 
    : countItemsByType(i => 
        i.field_type === 'employer' || i.item_type === 'employer' ||
        i.field_label?.toLowerCase().includes('employer')
      );
  
  const ssnCount = backendBreakdown?.ssn !== undefined ? backendBreakdown.ssn : 0;
  const phoneCount = backendBreakdown?.phone !== undefined ? backendBreakdown.phone : 0;
  const otherCount = backendBreakdown?.other !== undefined ? backendBreakdown.other : 0;
  
  // Check if we have any detailed breakdown
  const hasDetailedBreakdown = accountCount + inquiryCount + addressCount + nameCount + 
                               dobCount + employerCount + ssnCount + phoneCount + otherCount > 0;
  
  // DEBUG: Log breakdown for verification
  console.log(`[BREAKDOWN_DEBUG] Letter ${bureau}:`, {
    backendBreakdown,
    computed: { accountCount, inquiryCount, addressCount, nameCount, dobCount, employerCount },
    totalItemCount,
    disputedItemsLength: disputedItems.length
  });
  
  const methodLabels = {
    'data_breach': 'Data Breach / PI',
    'data_breach_method': 'Data Breach / PI',
    'personal_info_only': 'PI Accuracy',
    'personal_info_accuracy': 'PI Accuracy',
    'isolated': 'Isolated Strategy',
    'isolated_method': 'Isolated Strategy',
  };
  
  const letterType = methodLabels[method] || 'Dispute Letter';
  
  return (
    <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)] rounded-xl overflow-hidden shadow-sm" data-testid="letter-summary-card">
      <button 
        className="w-full px-4 py-3 bg-stone-50 dark:bg-[rgba(8,32,24,0.65)] border-b border-stone-200 dark:border-[rgba(142,218,182,0.12)] flex items-center justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <FileCheck size={18} className="text-emerald-600 dark:text-emerald-400" />
          <span className="font-semibold text-stone-900 dark:text-[#EAF7F0]">Letter Summary</span>
        </div>
        {isExpanded ? <ChevronUp size={18} className="dark:text-[#C7DDD0]" /> : <ChevronDown size={18} className="dark:text-[#C7DDD0]" />}
      </button>
      
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Basic Info */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-stone-500 dark:text-[#93AE9F]">Bureau</span>
              <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{bureau}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-stone-500 dark:text-[#93AE9F]">Letter Type</span>
              <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{letterType}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-stone-500 dark:text-[#93AE9F]">Total Items</span>
              <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{totalItemCount}</span>
            </div>
          </div>
          
          {/* Protected Anchors */}
          {protectedAnchors && (
            <div className="pt-3 border-t border-stone-100 dark:border-[rgba(142,218,182,0.10)]">
              <div className="flex items-center gap-2 mb-2">
                <Shield size={16} className="text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-semibold text-stone-900 dark:text-[#EAF7F0]">Protected Anchors Preserved</span>
              </div>
              <div className="space-y-1.5 pl-6">
                {protectedAnchors.legal_name && (
                  <div className="flex items-center gap-2 text-sm text-stone-600 dark:text-[#C7DDD0]">
                    <User size={14} className="text-blue-500 dark:text-blue-400" />
                    <span>Legal Name</span>
                    <CheckCircle size={12} className="text-emerald-500 dark:text-emerald-400 ml-auto" />
                  </div>
                )}
                {protectedAnchors.current_address && (
                  <div className="flex items-center gap-2 text-sm text-stone-600 dark:text-[#C7DDD0]">
                    <MapPin size={14} className="text-blue-500 dark:text-blue-400" />
                    <span>Current Address</span>
                    <CheckCircle size={12} className="text-emerald-500 dark:text-emerald-400 ml-auto" />
                  </div>
                )}
                {protectedAnchors.dob && (
                  <div className="flex items-center gap-2 text-sm text-stone-600 dark:text-[#C7DDD0]">
                    <Calendar size={14} className="text-blue-500 dark:text-blue-400" />
                    <span>Date of Birth</span>
                    <CheckCircle size={12} className="text-emerald-500 dark:text-emerald-400 ml-auto" />
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Items Disputed Breakdown */}
          <div className="pt-3 border-t border-stone-100 dark:border-[rgba(142,218,182,0.10)]">
            <div className="flex items-center gap-2 mb-2">
              <Layers size={16} className="text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-semibold text-stone-900 dark:text-[#EAF7F0]">Items Disputed</span>
            </div>
            {hasDetailedBreakdown ? (
              <div className="space-y-1.5 pl-6">
                {/* Show all categories - non-zero ones first */}
                {accountCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600 dark:text-[#C7DDD0]">Accounts</span>
                    <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{accountCount}</span>
                  </div>
                )}
                {inquiryCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600 dark:text-[#C7DDD0]">Inquiries</span>
                    <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{inquiryCount}</span>
                  </div>
                )}
                {addressCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600 dark:text-[#C7DDD0]">Addresses</span>
                    <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{addressCount}</span>
                  </div>
                )}
                {nameCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600 dark:text-[#C7DDD0]">Names/Aliases</span>
                    <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{nameCount}</span>
                  </div>
                )}
                {dobCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600 dark:text-[#C7DDD0]">DOB</span>
                    <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{dobCount}</span>
                  </div>
                )}
                {employerCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600 dark:text-[#C7DDD0]">Employers</span>
                    <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{employerCount}</span>
                  </div>
                )}
                {ssnCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600 dark:text-[#C7DDD0]">SSN</span>
                    <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{ssnCount}</span>
                  </div>
                )}
                {phoneCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600 dark:text-[#C7DDD0]">Phone</span>
                    <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{phoneCount}</span>
                  </div>
                )}
                {otherCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600 dark:text-[#C7DDD0]">Other</span>
                    <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{otherCount}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="pl-6 text-sm text-stone-600 dark:text-[#C7DDD0]">
                {totalItemCount > 0 ? `${totalItemCount} item${totalItemCount !== 1 ? 's' : ''} in this letter` : 'See letter for details'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// SIGNATURE UTILITIES
// ============================================

/**
 * Convert name to title case for signature display.
 * Example: "ATHANIO RENELUS" -> "Athanio Renelus"
 */
const toTitleCase = (name) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Validate and sanitize signature name.
 * - Max 60 characters
 * - Only letters, spaces, apostrophes, hyphens, periods
 * - Trim leading/trailing spaces
 * - Collapse duplicate spaces
 */
const sanitizeSignatureName = (name) => {
  if (!name) return '';
  
  // Remove HTML tags, scripts, emojis
  let sanitized = name
    .replace(/<[^>]*>/g, '') // Remove HTML
    .replace(/[\u{1F600}-\u{1F6FF}]/gu, '') // Remove emojis
    .replace(/[^\w\s'-\.]/g, '') // Only allow letters, numbers, spaces, apostrophes, hyphens, periods
    .trim()
    .replace(/\s+/g, ' '); // Collapse multiple spaces
  
  // Max 60 characters
  return sanitized.slice(0, 60);
};

/**
 * Validate signature name when enabled.
 * Returns error message or null if valid.
 */
const validateSignatureName = (name, enabled) => {
  if (!enabled) return null;
  if (!name || name.trim() === '') {
    return 'Enter a signature name or turn off digital signature.';
  }
  return null;
};

// ============================================
// DIGITAL SIGNATURE COMPONENT
// ============================================
export const DigitalSignaturePanel = ({ 
  consumerName, 
  signatureSettings, 
  onSignatureChange,
  applyToAll,
  onApplyToAllChange,
  onApplySignature,
  isAppliedToLetter = false,
  appliedSignature = null,
  hideHeader = false,
  defaultExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(hideHeader || defaultExpanded);
  const [localName, setLocalName] = useState('');
  const [localStyle, setLocalStyle] = useState('script');
  const [validationError, setValidationError] = useState(null);
  
  // All signature style options per requirements
  const signatureStyles = [
    { 
      id: 'script', 
      label: 'Script', 
      description: 'Smooth formal cursive',
      fontFamily: '"Brush Script MT", "Segoe Script", cursive',
      fontWeight: 400
    },
    { 
      id: 'elegant', 
      label: 'Elegant', 
      description: 'Refined signature-like cursive',
      fontFamily: '"Lucida Handwriting", "Segoe Script", cursive',
      fontWeight: 400
    },
    { 
      id: 'handwritten', 
      label: 'Handwritten', 
      description: 'Natural handwritten look',
      fontFamily: '"Segoe Print", "Comic Sans MS", cursive',
      fontWeight: 400
    },
    { 
      id: 'bold_handwritten', 
      label: 'Bold Handwritten', 
      description: 'Stronger signature look',
      fontFamily: '"Segoe Print", "Brush Script MT", cursive',
      fontWeight: 600
    },
    { 
      id: 'casual', 
      label: 'Casual Handwriting', 
      description: 'Relaxed personal signature',
      fontFamily: '"Comic Sans MS", "Segoe Print", cursive',
      fontWeight: 400
    },
    { 
      id: 'classic', 
      label: 'Classic Italic', 
      description: 'Professional italic fallback',
      fontFamily: 'Georgia, "Times New Roman", serif', 
      fontStyle: 'italic',
      fontWeight: 400
    },
  ];
  
  // Initialize local state from props or applied signature
  useEffect(() => {
    if (appliedSignature && appliedSignature.enabled) {
      // If there's an applied signature, load those values
      setLocalName(appliedSignature.name || '');
      setLocalStyle(appliedSignature.style || 'script');
    } else if (signatureSettings.enabled) {
      // Convert to title case for display if name looks like ALL CAPS
      const displayName = signatureSettings.name || toTitleCase(consumerName) || '';
      setLocalName(displayName);
      setLocalStyle(signatureSettings.style || 'script');
    } else {
      // When disabled, pre-fill with title-cased consumer name for convenience
      setLocalName(toTitleCase(consumerName) || '');
      setLocalStyle('script');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureSettings.enabled, consumerName, appliedSignature]);
  
  const currentStyle = signatureStyles.find(s => s.id === localStyle) || signatureStyles[0];
  
  // Handle name input change with sanitization
  const handleNameChange = (e) => {
    const sanitized = sanitizeSignatureName(e.target.value);
    setLocalName(sanitized);
    setValidationError(null);
  };
  
  // Handle style change
  const handleStyleChange = (styleId) => {
    setLocalStyle(styleId);
  };
  
  // Handle enable/disable toggle
  const handleEnableChange = (e) => {
    const enabled = e.target.checked;
    if (enabled) {
      // Pre-fill with title-cased consumer name
      const titleCaseName = toTitleCase(consumerName) || '';
      setLocalName(titleCaseName);
      onSignatureChange({ 
        ...signatureSettings, 
        enabled: true,
        name: titleCaseName,
        style: localStyle,
        applied: false
      });
    } else {
      // Disable and clear
      onSignatureChange({ 
        ...signatureSettings, 
        enabled: false,
        applied: false
      });
      setValidationError(null);
    }
  };
  
  // Handle Apply Signature button click
  const handleApplyClick = () => {
    // Validate
    const error = validateSignatureName(localName, signatureSettings.enabled);
    if (error) {
      setValidationError(error);
      return;
    }
    
    // Call parent apply handler with current values
    onApplySignature({
      enabled: signatureSettings.enabled,
      name: localName,
      style: localStyle,
      applyToAll: applyToAll
    });
  };
  
  // Determine if there are unsaved changes
  const hasUnsavedChanges = signatureSettings.enabled && (
    !isAppliedToLetter ||
    (appliedSignature && (
      localName !== appliedSignature.name ||
      localStyle !== appliedSignature.style
    ))
  );
  
  // Get display name for preview (use title case)
  const previewName = localName || toTitleCase(consumerName) || 'Your Name';
  
  // Determine button state
  const getButtonState = () => {
    if (!signatureSettings.enabled) {
      return { disabled: true, text: 'Apply Signature', variant: 'default' };
    }
    if (!localName || localName.trim() === '') {
      return { disabled: true, text: 'Apply Signature', variant: 'default' };
    }
    if (isAppliedToLetter && !hasUnsavedChanges) {
      return { disabled: true, text: 'Signature Applied', variant: 'applied' };
    }
    return { disabled: false, text: 'Apply Signature', variant: 'ready' };
  };
  
  const buttonState = getButtonState();
  
  return (
    <div className={`${hideHeader ? '' : 'bg-white dark:bg-[rgba(10,38,28,0.65)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)] rounded-xl overflow-hidden shadow-sm'}`} data-testid="signature-panel">
      {!hideHeader && (
        <button 
          type="button"
          className="w-full px-4 py-3 bg-stone-50 dark:bg-[rgba(8,32,24,0.65)] border-b border-stone-200 dark:border-[rgba(142,218,182,0.12)] flex items-center justify-between"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <PenTool size={18} className="text-purple-600 dark:text-purple-400" />
            <span className="font-semibold text-stone-900 dark:text-[#EAF7F0]">Digital Signature</span>
            <Badge variant="outline" className="text-xs ml-2 dark:bg-[rgba(84,106,93,0.25)] dark:text-[#C7DDD0] dark:border-[rgba(142,218,182,0.15)]">Optional</Badge>
          </div>
          {isExpanded ? <ChevronUp size={18} className="dark:text-[#C7DDD0]" /> : <ChevronDown size={18} className="dark:text-[#C7DDD0]" />}
        </button>
      )}
      
      {isExpanded && (
        <div className={`space-y-4 ${hideHeader ? '' : 'p-4'}`}>
          {/* Enable toggle - disabled by default */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={signatureSettings.enabled}
              onChange={handleEnableChange}
              className="w-5 h-5 rounded border-stone-300 dark:border-[rgba(142,218,182,0.20)] text-emerald-600 focus:ring-emerald-500 dark:bg-[rgba(10,38,28,0.50)]"
              data-testid="signature-enable-toggle"
            />
            <span className="text-sm font-medium text-stone-900 dark:text-[#EAF7F0]">Add typed digital signature</span>
          </label>
          
          {signatureSettings.enabled && (
            <>
              {/* Signature name input */}
              <div>
                <label className="block text-sm text-stone-600 dark:text-[#93AE9F] mb-1">Signature Name</label>
                <input
                  type="text"
                  value={localName}
                  onChange={handleNameChange}
                  placeholder={toTitleCase(consumerName) || 'Enter your name'}
                  maxLength={60}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-[rgba(10,38,28,0.50)] dark:text-[#EAF7F0] dark:placeholder-[#93AE9F] ${
                    validationError ? 'border-red-400 dark:border-red-500' : 'border-stone-300 dark:border-[rgba(142,218,182,0.20)]'
                  }`}
                  data-testid="signature-name-input"
                />
                {validationError && (
                  <p className="text-xs text-red-500 mt-1" data-testid="signature-validation-error">
                    {validationError}
                  </p>
                )}
                <p className="text-xs text-stone-400 dark:text-[#93AE9F] mt-1">
                  {localName.length}/60 characters
                </p>
              </div>
              
              {/* Style selector with visual previews */}
              <div>
                <label className="block text-sm text-stone-600 dark:text-[#93AE9F] mb-2">Signature Style</label>
                <div className="grid grid-cols-2 gap-2">
                  {signatureStyles.map(style => (
                    <button
                      key={style.id}
                      type="button"
                      onClick={() => setLocalStyle(style.id)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        localStyle === style.id
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-[rgba(16,185,129,0.15)] dark:border-emerald-400'
                          : 'border-stone-200 dark:border-[rgba(142,218,182,0.15)] hover:border-stone-300 dark:hover:border-[rgba(142,218,182,0.25)] dark:bg-[rgba(10,38,28,0.30)]'
                      }`}
                      data-testid={`signature-style-${style.id}`}
                    >
                      <div 
                        className="text-lg truncate dark:text-[#EAF7F0]"
                        style={{ 
                          fontFamily: style.fontFamily,
                          fontStyle: style.fontStyle || 'normal',
                          fontWeight: style.fontWeight || 400,
                          fontSize: '18px'
                        }}
                      >
                        {previewName.split(' ')[0] || 'Name'}
                      </div>
                      <div className="text-xs text-stone-500 dark:text-[#93AE9F] mt-1">{style.label}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Live preview - Full signature */}
              <div className="p-4 bg-stone-50 dark:bg-[rgba(10,38,28,0.50)] rounded-lg border border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
                <p className="text-xs text-stone-500 dark:text-[#93AE9F] mb-2">Full Preview:</p>
                <div 
                  className="signature-preview dark:text-[#EAF7F0]"
                  style={{ 
                    fontFamily: currentStyle.fontFamily,
                    fontStyle: currentStyle.fontStyle || 'normal',
                    fontWeight: currentStyle.fontWeight || 400,
                    fontSize: '24pt',
                    lineHeight: 1
                  }}
                  data-testid="signature-preview"
                >
                  {previewName}
                </div>
              </div>
              
              {/* Apply to all toggle */}
              <label className="flex items-center gap-3 cursor-pointer pt-2 border-t border-stone-100 dark:border-[rgba(142,218,182,0.10)]">
                <input
                  type="checkbox"
                  checked={applyToAll}
                  onChange={(e) => onApplyToAllChange(e.target.checked)}
                  className="w-4 h-4 rounded border-stone-300 dark:border-[rgba(142,218,182,0.20)] text-emerald-600 focus:ring-emerald-500 dark:bg-[rgba(10,38,28,0.50)]"
                  data-testid="apply-to-all-toggle"
                />
                <span className="text-sm text-stone-700 dark:text-[#C7DDD0]">Apply this signature to all generated letters</span>
              </label>
              
              {/* Apply Signature Button - Three states: default, ready, applied */}
              <Button
                onClick={handleApplyClick}
                disabled={buttonState.disabled}
                className={`w-full ${
                  buttonState.variant === 'applied' 
                    ? 'bg-stone-200 dark:bg-[rgba(84,106,93,0.25)] text-stone-500 dark:text-[#93AE9F] cursor-not-allowed' 
                    : buttonState.variant === 'ready'
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-stone-300 dark:bg-[rgba(84,106,93,0.25)] text-stone-500 dark:text-[#93AE9F]'
                }`}
                data-testid="apply-signature-btn"
              >
                <Check size={16} className="mr-2" />
                {buttonState.text}
              </Button>
              
              {/* Status indicator */}
              {isAppliedToLetter && !hasUnsavedChanges && (
                <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
                  <CheckCircle size={14} />
                  <span>Signature is showing in letter preview</span>
                </div>
              )}
              {hasUnsavedChanges && (
                <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                  <Info size={14} />
                  <span>Click Apply Signature to update the letter preview</span>
                </div>
              )}
              
              {/* Info note */}
              <p className="text-xs text-stone-500 dark:text-[#93AE9F] italic">
                Optional: Add a typed signature style to your downloaded PDF. You can also print and sign manually.
              </p>
            </>
          )}
          
          {/* When disabled, show note about manual signature space */}
          {!signatureSettings.enabled && (
            <p className="text-xs text-stone-500 dark:text-[#93AE9F] italic">
              A blank space will be reserved above your printed name for manual signature.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// VALIDATION BADGES
// ============================================
const ValidationBadges = ({ validationStatus }) => {
  const badges = [
    { 
      key: 'bureau_specific', 
      label: 'Bureau-Specific Values Verified', 
      icon: Building2,
      passed: validationStatus?.bureau_specific !== false 
    },
    { 
      key: 'protected_anchors', 
      label: 'Protected Anchors Preserved', 
      icon: Shield,
      passed: validationStatus?.protected_anchors !== false 
    },
    { 
      key: 'pdf_ready', 
      label: 'PDF Ready', 
      icon: FileCheck,
      passed: validationStatus?.pdf_ready !== false 
    },
    { 
      key: 'no_leakage', 
      label: 'No Cross-Bureau Leakage', 
      icon: Lock,
      passed: validationStatus?.no_leakage !== false 
    },
  ];
  
  return (
    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-4 min-w-0" data-testid="validation-badges">
      {badges.map(badge => (
        <Badge
          key={badge.key}
          variant={badge.passed ? 'default' : 'destructive'}
          className={`
            flex items-start sm:items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-[11px] sm:text-xs h-auto whitespace-normal text-left leading-snug
            ${badge.passed 
              ? 'bg-emerald-50 dark:bg-[rgba(16,185,129,0.12)] text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-[rgba(16,185,129,0.25)]' 
              : 'bg-red-50 dark:bg-[rgba(239,68,68,0.12)] text-red-700 dark:text-red-300 border-red-200 dark:border-[rgba(239,68,68,0.25)]'
            }
          `}
        >
          <badge.icon size={14} className="shrink-0 mt-0.5 sm:mt-0" />
          <span className="break-words">{badge.label}</span>
          {badge.passed && <CheckCircle size={12} className="ml-auto sm:ml-1 shrink-0" />}
        </Badge>
      ))}
    </div>
  );
};

// ============================================
// DOCUMENT VIEWER WITH ZOOM AND PRINT CSS
// ============================================
const LETTER_PAGE_WIDTH_PX = 816;
const LETTER_PAGE_MIN_HEIGHT_PX = 1056;

const DocumentViewer = ({ html, zoom, onZoomChange, signatureSettings, consumerName }) => {
  const iframeRef = useRef(null);
  const viewportRef = useRef(null);
  const [contentWidth, setContentWidth] = useState(LETTER_PAGE_WIDTH_PX);
  const [contentHeight, setContentHeight] = useState(LETTER_PAGE_MIN_HEIGHT_PX);
  const [fitScale, setFitScale] = useState(1);
  
  const zoomLevels = [50, 75, 100, 125, 150];
  const displayScale = fitScale * (zoom / 100);
  
  const handleZoomIn = () => {
    const currentIdx = zoomLevels.indexOf(zoom);
    if (currentIdx < zoomLevels.length - 1) {
      onZoomChange(zoomLevels[currentIdx + 1]);
    }
  };
  
  const handleZoomOut = () => {
    const currentIdx = zoomLevels.indexOf(zoom);
    if (currentIdx > 0) {
      onZoomChange(zoomLevels[currentIdx - 1]);
    }
  };
  
  const handleFitWidth = () => onZoomChange(100);

  const measureIframeContent = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc?.body) return;
    try {
      const body = doc.body;
      const htmlEl = doc.documentElement;
      const height = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        htmlEl?.scrollHeight || 0,
        htmlEl?.offsetHeight || 0,
        LETTER_PAGE_MIN_HEIGHT_PX,
      );
      const width = Math.max(
        body.scrollWidth,
        body.offsetWidth,
        htmlEl?.scrollWidth || 0,
        htmlEl?.offsetWidth || 0,
        LETTER_PAGE_WIDTH_PX,
      );
      setContentHeight(height);
      setContentWidth(width);
    } catch {
      // iframe content is same-origin from doc.write
    }
  }, []);
  
  // Signature style definitions matching the panel
  const SIGNATURE_STYLES = {
    script: { fontFamily: '"Brush Script MT", "Segoe Script", cursive', fontWeight: 400 },
    elegant: { fontFamily: '"Lucida Handwriting", "Segoe Script", cursive', fontWeight: 400 },
    handwritten: { fontFamily: '"Segoe Print", "Comic Sans MS", cursive', fontWeight: 400 },
    bold_handwritten: { fontFamily: '"Segoe Print", "Brush Script MT", cursive', fontWeight: 600 },
    casual: { fontFamily: '"Comic Sans MS", "Segoe Print", cursive', fontWeight: 400 },
    classic: { fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic', fontWeight: 400 },
  };
  
  useEffect(() => {
    if (iframeRef.current && html) {
      const doc = iframeRef.current.contentDocument;
      doc.open();
      
      // Get signature style configuration
      const styleKey = signatureSettings?.style || 'script';
      const styleConfig = SIGNATURE_STYLES[styleKey] || SIGNATURE_STYLES.script;
      const sigFont = styleConfig.fontFamily;
      const sigWeight = styleConfig.fontWeight || 400;
      const sigFontStyle = styleConfig.fontStyle || 'normal';
      
      // Match Download Printable Letter layout — hide debug banners only.
      const signatureCss = `
<style type="text/css">
.signature-block { margin-top: 18pt; }
.signature-slot { height: 38pt; display: flex; align-items: center; }
.digital-signature {
  font-family: ${sigFont};
  font-size: 24pt;
  font-weight: ${sigWeight};
  font-style: ${sigFontStyle};
  line-height: 1;
}
.signature-style-script { font-family: "Brush Script MT", "Segoe Script", cursive; }
.signature-style-elegant { font-family: "Lucida Handwriting", "Segoe Script", cursive; }
.signature-style-handwritten { font-family: "Segoe Print", "Comic Sans MS", cursive; }
.signature-style-bold_handwritten { font-family: "Segoe Print", "Brush Script MT", cursive; font-weight: 600; }
.signature-style-casual { font-family: "Comic Sans MS", "Segoe Print", cursive; }
.signature-style-classic { font-family: Georgia, "Times New Roman", serif; font-style: italic; }
</style>
`;

      let modifiedHtml = injectPrintablePreviewCss(html);
      
      // Determine if signature should be shown (must be enabled AND applied)
      const showDigitalSignature = signatureSettings?.enabled && 
                                   signatureSettings?.applied && 
                                   signatureSettings?.name;
      
      console.log('[DocumentViewer] Signature settings:', {
        enabled: signatureSettings?.enabled,
        applied: signatureSettings?.applied,
        name: signatureSettings?.name,
        showDigitalSignature
      });
      
      // Build signature block HTML
      // Always include a signature slot (blank or with digital signature)
      // Signature appears between "Sincerely," and the printed name
      const signatureSlotHtml = showDigitalSignature
        ? `<div class="signature-slot"><div class="digital-signature signature-style-${styleKey}">${signatureSettings.name}</div></div>`
        : `<div class="signature-slot"></div>`; // Blank space for manual signature
      
      // Insert signature block after "Sincerely," closing div and before the printed name
      // The letter structure is: <div class="closing"><p>Sincerely,</p></div><div class="signature"><p>NAME</p></div>
      
      // Strategy 1: Try to insert between closing div and signature div
      if (modifiedHtml.includes('class="closing"') && modifiedHtml.includes('class="signature"')) {
        modifiedHtml = modifiedHtml.replace(
          /(<div class="closing">[\s\S]*?<\/div>)\s*(<div class="signature">)/i,
          `$1\n<div class="signature-block">${signatureSlotHtml}</div>\n$2`
        );
        console.log('[DocumentViewer] Injected signature between closing and signature divs');
      }
      // Strategy 2: Try to insert after Sincerely </p></div>
      else if (modifiedHtml.includes('Sincerely')) {
        modifiedHtml = modifiedHtml.replace(
          /(Sincerely,?\s*<\/p>\s*<\/div>)/i,
          `$1\n<div class="signature-block">${signatureSlotHtml}</div>`
        );
        console.log('[DocumentViewer] Injected signature after Sincerely closing');
      }
      // Strategy 3: Simple insertion after Sincerely,</p>
      else if (modifiedHtml.match(/Sincerely,?\s*<\/p>/i)) {
        modifiedHtml = modifiedHtml.replace(
          /(Sincerely,?\s*<\/p>)/i,
          `$1\n<div class="signature-block">${signatureSlotHtml}</div>`
        );
        console.log('[DocumentViewer] Injected signature after Sincerely p tag');
      }
      
      // Inject signature CSS without overriding printable letter margins
      if (modifiedHtml.includes('</head>')) {
        modifiedHtml = modifiedHtml.replace('</head>', `${signatureCss}</head>`);
      } else if (modifiedHtml.includes('<body')) {
        modifiedHtml = modifiedHtml.replace(/(<body[^>]*>)/, `$1${signatureCss}`);
      } else {
        modifiedHtml = signatureCss + modifiedHtml;
      }
      
      doc.write(modifiedHtml);
      doc.close();

      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(measureIframeContent);
      });
      const timer = window.setTimeout(measureIframeContent, 150);
      return () => {
        cancelAnimationFrame(raf);
        window.clearTimeout(timer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, signatureSettings, consumerName, measureIframeContent]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const updateFitScale = () => {
      const padding = 16;
      const available = viewport.clientWidth - padding;
      if (available <= 0 || contentWidth <= 0) return;
      setFitScale(Math.min(1, available / contentWidth));
    };

    updateFitScale();
    const ro = new ResizeObserver(updateFitScale);
    ro.observe(viewport);
    window.addEventListener('resize', updateFitScale);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateFitScale);
    };
  }, [contentWidth]);
  
  return (
    <div className="flex flex-col printable-letter-container min-w-0" data-testid="document-viewer">
      {/* Zoom Controls - hidden during print */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between bg-stone-100 dark:bg-[rgba(8,32,24,0.65)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)] rounded-t-lg px-3 sm:px-4 py-2 no-print min-w-0">
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            disabled={zoom <= zoomLevels[0]}
            className="h-8 w-8 p-0 dark:text-[#C7DDD0] dark:hover:bg-[rgba(142,218,182,0.10)]"
            data-testid="zoom-out-btn"
          >
            <ZoomOut size={16} />
          </Button>
          
          <span className="text-sm font-medium text-stone-700 dark:text-[#C7DDD0] min-w-[50px] text-center">
            {zoom}%
          </span>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            disabled={zoom >= zoomLevels[zoomLevels.length - 1]}
            className="h-8 w-8 p-0"
            data-testid="zoom-in-btn"
          >
            <ZoomIn size={16} />
          </Button>
          
          <div className="h-4 w-px bg-stone-300 dark:bg-[rgba(142,218,182,0.20)] mx-2" />
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleFitWidth}
            className="text-xs h-8 px-2 dark:text-[#C7DDD0] dark:hover:bg-[rgba(142,218,182,0.10)]"
          >
            <Maximize2 size={14} className="mr-1" />
            <span className="hidden min-[400px]:inline">Fit Width</span>
            <span className="min-[400px]:hidden">Fit</span>
          </Button>
        </div>
        
        <div className="hidden sm:flex items-center gap-2 text-xs text-stone-500 dark:text-[#93AE9F]">
          <BadgeCheck size={14} className="text-emerald-600 dark:text-emerald-400" />
          Preview matches PDF layout
        </div>
        <p className="sm:hidden text-[11px] text-stone-500 dark:text-[#93AE9F] leading-snug">
          Letter auto-fits your screen. Use zoom to enlarge.
        </p>
      </div>
      
      {/* Document Container — scales letter to fit mobile viewport width */}
      <div 
        ref={viewportRef}
        className="step5-letter-preview-viewport bg-stone-200 dark:bg-[rgba(8,32,24,0.50)] p-2 sm:p-4 overflow-y-auto overflow-x-auto rounded-b-lg min-w-0"
        style={{ maxHeight: 'min(800px, 70vh)' }}
      >
        <div className="flex justify-center min-w-0 w-full">
          <div
            className="relative"
            style={{
              width: Math.max(1, Math.round(contentWidth * displayScale)),
              height: Math.max(1, Math.round(contentHeight * displayScale)),
            }}
          >
            <div
              className="bg-white shadow-xl rounded-sm printable-letter"
              style={{
                width: contentWidth,
                height: contentHeight,
                transform: `scale(${displayScale})`,
                transformOrigin: 'top left',
              }}
            >
              <iframe
                ref={iframeRef}
                title="Letter Preview"
                className="block border-0 w-full"
                style={{
                  width: contentWidth,
                  height: contentHeight,
                  background: '#ffffff',
                }}
                data-testid="letter-preview-iframe"
              />
            </div>
          </div>
        </div>
        
        {/* Page indicator */}
        <div className="text-center mt-3 sm:mt-4 text-sm text-stone-500 dark:text-[#93AE9F] no-print">
          Page 1
        </div>
      </div>
    </div>
  );
};

// ============================================
// PARITY NOTICE
// ============================================
const ParityNotice = () => (
  <div className="bg-emerald-50 dark:bg-[rgba(16,185,129,0.12)] border border-emerald-200 dark:border-[rgba(16,185,129,0.20)] rounded-lg p-3 flex items-start gap-3" data-testid="parity-notice">
    <BadgeCheck size={18} className="text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
    <div>
      <p className="text-sm text-emerald-800 dark:text-emerald-200">
        <strong>Preview matches the downloaded PDF layout.</strong> What you see here is exactly what will be printed and mailed.
      </p>
    </div>
  </div>
);

// ============================================
// PRINT CSS - Added to document head
// ============================================
const PrintStyles = () => (
  <style>{`
    @media print {
      body * {
        visibility: hidden;
      }
      .printable-letter,
      .printable-letter * {
        visibility: visible !important;
      }
      .printable-letter {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
      }
      .printable-letter iframe {
        width: 100% !important;
        height: auto !important;
        min-height: 11in !important;
      }
      .no-print {
        display: none !important;
      }
      @page {
        size: letter;
        margin: 0.5in 0.6in;
      }
    }
  `}</style>
);

// ============================================
// MAIN ENHANCED LETTER PREVIEW COMPONENT
// ============================================
const EnhancedLetterPreview = ({
  letters,
  currentIndex,
  onSelectLetter,
  onDownload,
  onDownloadAll,
  onPrint,
  onBack,
  onApprove,
  downloading,
  protectedAnchors,
  validationStatus,
  consumerName,
  backButtonLabel = "Back to Revise",
}) => {
  const [zoom, setZoom] = useState(100);
  // Signature disabled by default per requirements
  const [signatureSettings, setSignatureSettings] = useState({
    enabled: false,
    name: '',
    style: 'script',
    applied: false, // Track if user has clicked "Apply Signature"
  });
  const [applySignatureToAll, setApplySignatureToAll] = useState(false);
  // Track applied signatures per letter - this is what the DocumentViewer uses
  const [appliedSignatures, setAppliedSignatures] = useState({});
  // Force re-render key for iframe
  const [renderKey, setRenderKey] = useState(0);
  
  const currentLetter = letters?.[currentIndex];
  
  // DO NOT auto-populate signature name - user must enable and type
  // This follows the "disabled by default" requirement
  
  if (!currentLetter) {
    return (
      <div className="flex items-center justify-center h-96 text-stone-500">
        <Loader2 size={24} className="animate-spin mr-2" />
        Loading letter preview...
      </div>
    );
  }
  
  // Handle Apply Signature callback - THIS is where the magic happens
  const handleApplySignature = (signatureData) => {
    console.log('[Apply Signature] Input:', signatureData);
    
    const appliedSig = {
      enabled: signatureData.enabled,
      name: signatureData.name,
      style: signatureData.style,
      applied: true
    };
    
    console.log('[Apply Signature] Creating signature:', appliedSig);
    
    if (signatureData.applyToAll) {
      // Apply to ALL letters in current round
      const newAppliedSignatures = {};
      letters.forEach((letter, idx) => {
        newAppliedSignatures[idx] = { ...appliedSig };
      });
      console.log('[Apply Signature] Applying to all:', newAppliedSignatures);
      setAppliedSignatures(newAppliedSignatures);
    } else {
      // Apply only to current letter
      console.log('[Apply Signature] Applying to current letter index:', currentIndex);
      setAppliedSignatures(prev => {
        const updated = {
          ...prev,
          [currentIndex]: { ...appliedSig }
        };
        console.log('[Apply Signature] Updated appliedSignatures:', updated);
        return updated;
      });
    }
    
    // Update the panel settings to mark as applied
    setSignatureSettings(prev => ({
      ...prev,
      applied: true
    }));
    
    // Force re-render of the iframe by changing the key
    setRenderKey(prev => {
      console.log('[Apply Signature] Incrementing renderKey from', prev, 'to', prev + 1);
      return prev + 1;
    });
  };
  
  // Get effective signature settings for current letter (used by DocumentViewer)
  // This is recalculated on every render when appliedSignatures changes
  const effectiveSignature = appliedSignatures[currentIndex] || { enabled: false, name: '', style: 'script', applied: false };
  
  console.log('[EnhancedLetterPreview] Rendering with:', {
    currentIndex,
    renderKey,
    appliedSignaturesKeys: Object.keys(appliedSignatures),
    effectiveSignature,
    hasAppliedSignature: !!appliedSignatures[currentIndex]
  });
  
  // Check if current letter has signature applied (for panel display)
  const isSignatureAppliedToCurrentLetter = appliedSignatures[currentIndex]?.applied === true;
  
  const handlePrint = () => {
    // Get the iframe and print only its content
    const iframe = document.querySelector('[data-testid="letter-preview-iframe"]');
    if (iframe?.contentWindow) {
      iframe.contentWindow.print();
    } else {
      window.print();
    }
  };
  
  const handleDownload = () => {
    // Pass signature settings to download handler - only if enabled AND applied
    const letterWithSignature = {
      ...currentLetter,
      signatureSettings: (effectiveSignature.enabled && effectiveSignature.applied) 
        ? effectiveSignature 
        : null,
    };
    onDownload(letterWithSignature);
  };
  
  return (
    <div className="min-h-screen bg-stone-100 dark:bg-[#0a1f17] py-4 px-2 sm:py-6 sm:px-4 min-w-0 overflow-x-hidden pb-28 md:pb-6" data-testid="enhanced-letter-preview">
      <PrintStyles />
      
      <div className="max-w-7xl mx-auto min-w-0">
        {/* Step indicator */}
        <div className="mb-3 sm:mb-4 flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2 text-sm text-stone-600 dark:text-[#C7DDD0] no-print min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.15)] text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-[rgba(16,185,129,0.25)] shrink-0">
              Step 5
            </Badge>
            <span className="font-medium">Letters Ready for Review</span>
          </div>
          <div className="flex items-center gap-2 text-stone-500 dark:text-[#93AE9F]">
            <span className="hidden sm:inline">•</span>
            <span>{letters.length} letter{letters.length !== 1 ? 's' : ''} generated</span>
          </div>
        </div>
        
        {/* Bureau Tabs - NO colored dots */}
        <div className="no-print">
          <BureauTabSelector 
            letters={letters} 
            currentIndex={currentIndex} 
            onSelectLetter={onSelectLetter} 
          />
        </div>
        
        {/* Validation Badges */}
        <div className="no-print">
          <ValidationBadges validationStatus={validationStatus} />
        </div>
        
        {/* Preview Header - NO Copy button */}
        <div className="no-print">
          <PreviewHeader
            letter={currentLetter}
            totalLetters={letters.length}
            currentIndex={currentIndex}
            onDownload={handleDownload}
            onPrint={handlePrint}
            onBack={onBack}
            downloading={downloading}
            backButtonLabel={backButtonLabel}
          />
        </div>
        
        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Left Sidebar - Summary Cards */}
          <div className="lg:col-span-1 space-y-4 no-print">
            <LetterSummaryCard 
              letter={currentLetter} 
              protectedAnchors={protectedAnchors}
            />
            
            <DigitalSignaturePanel
              consumerName={consumerName || currentLetter?.consumer_name}
              signatureSettings={signatureSettings}
              onSignatureChange={(newSettings) => {
                // When settings change, mark as not applied yet
                setSignatureSettings({
                  ...newSettings,
                  applied: false
                });
              }}
              applyToAll={applySignatureToAll}
              onApplyToAllChange={setApplySignatureToAll}
              onApplySignature={handleApplySignature}
              isAppliedToLetter={isSignatureAppliedToCurrentLetter}
              appliedSignature={appliedSignatures[currentIndex]}
            />
            
            <ParityNotice />
          </div>
          
          {/* Right Content - Document Viewer */}
          <div className="lg:col-span-3">
            <DocumentViewer 
              key={`viewer-${currentIndex}-${renderKey}`}
              html={resolvePrintablePreviewHtml(currentLetter)}
              zoom={zoom}
              onZoomChange={setZoom}
              signatureSettings={effectiveSignature}
              consumerName={consumerName || currentLetter?.consumer_name}
            />
          </div>
        </div>
        
        {/* Bottom Actions — full-width stacked buttons on mobile; sticky so actions stay reachable */}
        <div className="mt-4 sm:mt-6 sticky bottom-0 z-30 md:static md:z-auto -mx-1 px-1 sm:mx-0 sm:px-0 pb-[env(safe-area-inset-bottom,0px)] no-print">
          <div className="flex flex-col gap-3 bg-white dark:bg-[rgba(10,38,28,0.95)] backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-stone-200 dark:border-[rgba(142,218,182,0.15)] shadow-lg md:shadow-sm min-w-0 max-w-full box-border">
            <div className="text-sm text-stone-600 dark:text-[#C7DDD0] break-words leading-relaxed">
              {letters.length > 1 && (
                <span>
                  Viewing letter {currentIndex + 1} of {letters.length}. 
                  Swipe the bureau tabs above to switch letters.
                </span>
              )}
            </div>
            
            <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto md:ml-auto md:justify-end">
              {letters.length > 1 && (
                <Button
                  variant="outline"
                  onClick={onDownloadAll}
                  disabled={downloading}
                  className="w-full max-w-full md:w-auto whitespace-normal h-auto min-h-9 py-2.5 order-2 md:order-1 dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0] dark:hover:bg-[rgba(142,218,182,0.08)]"
                  data-testid="download-all-btn"
                >
                  <Download size={16} className="mr-2 shrink-0" />
                  <span className="hidden min-[400px]:inline">Download All PDFs</span>
                  <span className="min-[400px]:hidden">Download All</span>
                </Button>
              )}
              
              <Button
                onClick={onApprove}
                className="w-full max-w-full md:w-auto whitespace-normal h-auto min-h-9 py-2.5 order-1 md:order-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="approve-continue-btn"
              >
                <Check size={16} className="mr-2 shrink-0" />
                <span className="hidden min-[400px]:inline">Approve & Continue</span>
                <span className="min-[400px]:hidden">Approve</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedLetterPreview;
